<template>
  <div class="tiktok-ads">
    <div v-for="(ad, index) in ads" :key="ad.id ?? index" class="ad-container">
      <div class="ad-header">
        <div class="form-label">Ad #{{ index + 1 }}{{ ad.ad_name ? ` — ${ad.ad_name}` : '' }}</div>
        <LeadButton
          v-if="ads.length > 1"
          variant="icon"
          :title="$t('campaigns.tiktok.remove_ad')"
          class="remove-ad-btn"
          @click="removeAd(index)">
          <IconTrash :size="16" />
        </LeadButton>
      </div>

      <div class="ad-text-wrapper">
        <LeadInput
          :value="ad.ad_text ?? ''"
          @update:model-value="(value) => updateAdText(index, value)"
          type="textarea"
          :rows="4"
          :label="$t('campaigns.tiktok.ad_text')"
          :errors="errorsForAd(index, 'ad_text')" />
        <div class="char-counter" :class="{ 'is-over': (ad.ad_text?.length ?? 0) > MAX_AD_TEXT_LENGTH }">
          {{ ad.ad_text?.length ?? 0 }} / {{ MAX_AD_TEXT_LENGTH }}
        </div>
      </div>

      <div class="video-section">
        <div class="form-label">{{ $t('campaigns.tiktok.video') }}</div>
        <div class="images-grid">
          <AdImagePicker
            v-if="ad.local_video_id"
            v-bind="getBannerPickerState(ad.local_video_id)"
            boxSize
            @click="openVideoPicker(index)"
            @remove="removeVideo(index)" />
          <AdImagePicker
            v-else
            boxSize
            :uploadText="$t('campaigns.tiktok.select_video')"
            @click="openVideoPicker(index)" />
        </div>
      </div>
      <FormErrorList class="ms-1" :errors="errorsForAd(index, 'local_video_id')" />
    </div>
  </div>

  <ImagePickerModal
    v-model="videoPickerOpen"
    :images="videoPickerImages"
    :selectedIds="currentlySelectedIds"
    :title="$t('campaigns.choose_video')"
    :uploadText="$t('campaigns.tiktok.select_video')"
    @select="selectVideoById" />
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import LeadInput from './LeadInput.vue';
import LeadButton from '../generic/LeadButton.vue';
import AdImagePicker from './AdImagePicker.vue';
import ImagePickerModal from './ImagePickerModal.vue';
import { FormErrorList } from '@prospective/lithium';
import { IconTrash } from '@tabler/icons-vue';
import { fetchOrderBanners } from '@/api/banner';
import type { IBanner } from '@/types/banner';
import { useProviderStore } from '@/stores/providers';
import { bannerDisplayState, bannerThumbnailState } from '@/helpers/bannerState';

const MAX_AD_TEXT_LENGTH = 100;

interface TikTokAd {
  id: number | null;
  ad_text: string | null;
  ad_format: string | null;
  local_video_id: number | null;
  ad_name: string | null;
}

const props = defineProps<{
  modelValue: TikTokAd[];
  errors?: any;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: TikTokAd[]];
}>();

const ads = ref<TikTokAd[]>([]);
const banners = ref<IBanner[]>([]);
const videoPickerOpen = ref(false);
const currentAdIndex = ref(0);

const providerStore = useProviderStore();
const { currentOrderId } = storeToRefs(providerStore);

// tiktok ads only accept a video, so image-only banners are left out. banners that rendered
// nothing at all are kept so a failed render stays visible instead of silently disappearing —
// same rule as filteredBanners in pjas-frontend's BannerComponent.vue
const videoBanners = computed(() =>
  banners.value.filter(
    (b) => b.designer_template_id !== null && (b.render_video_id !== null || b.render_image_id === null)
  )
);

const videoPickerImages = computed(() =>
  videoBanners.value.map((b) => ({
    id: b.render_video_id ?? -b.id,
    disabled: !b.render_video_id || !!b.render_error || (b.render_video?.data?.status ?? 0) < 0,
    ...bannerThumbnailState(b),
  }))
);

const currentlySelectedIds = computed(() => {
  const id = ads.value[currentAdIndex.value]?.local_video_id;
  return id ? [id] : [];
});

const getBannerPickerState = (localVideoId: number) => {
  const banner = banners.value.find((b) => b.render_video_id === localVideoId);
  return bannerDisplayState(banner, banner?.render_image_id ?? null);
};

const extractMessages = (obj: any): string[] => {
  const result: string[] = [];
  for (const key in obj) {
    if (Array.isArray(obj[key])) result.push(...obj[key]);
    else if (typeof obj[key] === 'object' && obj[key] !== null) result.push(...extractMessages(obj[key]));
    else if (typeof obj[key] === 'string') result.push(obj[key]);
  }
  return result;
};

const errorsForAd = (index: number, subKey?: string): string[] => {
  const adErrors = props.errors?.['ad_' + (index + 1)];
  if (!adErrors) return [];
  if (Array.isArray(adErrors)) return adErrors;
  if (subKey) return extractMessages(adErrors[subKey]);
  return extractMessages(adErrors);
};

const updateAdText = (index: number, value: string) => {
  ads.value[index] = { ...ads.value[index], ad_text: value };
  emit('update:modelValue', [...ads.value]);
};

onMounted(() => {
  fetchOrderBanners(currentOrderId.value ?? '').then((res) => {
    banners.value = res?.data || [];
  });
});

const openVideoPicker = (adIndex: number) => {
  currentAdIndex.value = adIndex;
  videoPickerOpen.value = true;
  fetchOrderBanners(currentOrderId.value ?? '').then((res) => {
    banners.value = res?.data || [];
  });
};

const selectVideoById = (id: number) => {
  const current = [...ads.value];
  const ad = current[currentAdIndex.value];
  current[currentAdIndex.value] = {
    ...ad,
    local_video_id: ad.local_video_id === id ? null : id,
  };
  ads.value = current;
  emit('update:modelValue', [...ads.value]);
};

const removeVideo = (adIndex: number) => {
  const current = [...ads.value];
  current[adIndex] = { ...current[adIndex], local_video_id: null };
  ads.value = current;
  emit('update:modelValue', [...ads.value]);
};

const removeAd = (index: number) => {
  ads.value = ads.value.filter((_, i) => i !== index);
  emit('update:modelValue', [...ads.value]);
};

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue && newValue.length > 0) {
      ads.value = newValue.map((ad) => ({
        id: ad.id ?? null,
        ad_text: ad.ad_text ?? null,
        ad_format: ad.ad_format ?? null,
        local_video_id: ad.local_video_id ?? null,
        ad_name: ad.ad_name ?? null,
      }));
    } else {
      ads.value = [];
    }
  },
  { immediate: true, deep: true }
);
</script>

<style scoped lang="scss">
.ad-container {
  & + & {
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid #e4e7ec;
  }
}

.ad-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;

  .remove-ad-btn {
    margin-left: auto;
  }
}

.ad-format-badge {
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 4px;
  background: #f2f4f7;
  color: #475467;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.ad-text-wrapper {
  position: relative;
  margin-bottom: 20px;
}

.char-counter {
  position: absolute;
  top: 0px;
  right: 2px;
  font-size: 12px;
  color: #667085;
  margin-top: 4px;

  &.is-over {
    color: #ef4444;
    font-weight: 500;
  }
}

.video-section {
  margin-top: 4px;
}

.images-grid {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

:deep(.form-label) {
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  margin-bottom: 6px;
  color: #344054;
}
</style>
